import { Button, Card, colors, H3, PersonBlock, Row, Separator, Skeleton, useOverlay } from '@fs/zion-ui'
import React, { type ComponentProps, useCallback } from 'react'
import { Person } from '@fs/zion-person'
import { SelectPersonOverlay } from '@fs/zion-tree-groups'
import { useTranslation } from 'react-i18next'
import { useStartingPerson } from '../../providers/StartingPersonProvider'
import { useMembersContext } from '../../providers/MembersProvider'
import { useUserIsDataAdmin } from '../../providers/DataAdminProvider'

export default function StartingPerson(): JSX.Element {
  const [t] = useTranslation()
  const startingPersonOverlay = useOverlay()
  const { startingPersonId, setStartingPersonId, loading } = useStartingPerson()
  const onSelectStartingPerson = useCallback<NonNullable<ComponentProps<typeof SelectPersonOverlay>['onSelect']>>(
    (person) => {
      if (person.id !== 'UNKNOWN') {
        setStartingPersonId(person.id)
      }
      startingPersonOverlay.close()
    },
    [setStartingPersonId, startingPersonOverlay]
  )
  const { userIsAdmin } = useMembersContext()
  const { userIsDataAdmin } = useUserIsDataAdmin()

  return (
    <div>
      <H3 size="H6">{t('group-management.starting-person-tree.heading')}</H3>
      <Separator size="nano" />
      <Card backgroundColor={colors.gray03} edges="none" margins="xs">
        <Row alignY="middle">
          {startingPersonId || loading ? (
            <Person avatarSize="smaller" personId={startingPersonId} loading={loading} />
          ) : (
            <PersonBlock avatarSize="bigger" name={t('group-management.no-starting-person-selected.label')} size="sm" />
          )}

          <div growX />
          {userIsAdmin && !userIsDataAdmin && (
            <Button data-testid="change-starting-person-button" onClick={startingPersonOverlay.handleClick}>
              {t('group-management.change.label')}
            </Button>
          )}
        </Row>

        {(startingPersonOverlay.isOpen || startingPersonOverlay.transitioning) && (
          <SelectPersonOverlay
            mode="startingPerson"
            onSelect={onSelectStartingPerson}
            overlay={startingPersonOverlay}
          />
        )}
      </Card>
    </div>
  )
}

export function StartingPersonSkeleton(): JSX.Element {
  return (
    <div>
      <Skeleton.H6 />

      <Separator size="nano" />

      <Card backgroundColor={colors.gray03} edges="none" margins="xs">
        <Person avatarSize="smaller" loading />
      </Card>
    </div>
  )
}
